import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { StepObject } from '../../App';
import Utils from '../../common/helpers/Utils';
import {
  DataEvents, CustomDataEvents, DataCategories, DataActions, EcommerceEvents,
} from '../../types/enum';
import { getQuote } from '../quote/QuoteSlice';

interface ProgressProps {
  step?: number;
  stepObject: StepObject;
}

interface Step {
  title: string;
  steps: any;
}

const steps = [
  {
    title: 'Step 1',
    steps: [
      {
        title: 'Your Details',
      },
      {
        title: 'Policy Start Date',
      },
      {
        title: 'Your Business',
      },
      {
        title: 'Tailor Your Package',
      },
    ],
  },
  {
    title: 'Step 2',
    steps: [
      {
        title: 'Your Quote',
      },
      {
        title: 'Your Quote',
      },
    ],
  },
  {
    title: 'Step 3',
    steps: [
      {
        title: 'Contact Details',
      },
    ],
  },
  {
    title: 'Step 4',
    steps: [
      {
        title: 'Review',
      },
      {
        title: 'Payment',
      },
      {
        title: 'Complete',
      },
    ],
  },
];

function Progress(props: ProgressProps) {
  const { stepObject } = props;
  const quoteData = useSelector(getQuote);
  const { innerStep, step: outerStep } = quoteData.step;

  useEffect(
    () => {
      const isRenewal = window.kbSchema.is_renewal;
      if (
        outerStep === 3
        && innerStep === 2
        && window.kbSchema.ga_settings?.ecommerce
        && !window.kbSchema.has_finalised_purchase
      ) {
        const purchaseEventData = window.kbSchema.ga_settings.ecommerce;
        const purchaseEventDataItems = purchaseEventData.items?.map(
          (item: { [key: string]: string; }) => ({
            item_name: item.name,
            item_id: `${item.id}-${item.sku}`,
            price: item.price,
            sku: item.sku,
            category: item.category,
            quantity: item.quantity,
            affiliation: item.affiliation,
          }),
        );
        Utils.sendEcommerceEvent({
          event: EcommerceEvents[EcommerceEvents.purchase],
          ecommerce: {
            currency: 'GBP',
            transaction_id: purchaseEventData.transaction?.id,
            tax: purchaseEventData.transaction?.tax,
            value: window.kbSchema.transaction?.purchase_amount ?? 0.00,
            coupon: window.kbSchema.transaction?.promo_code ?? 'N/A',
            items: purchaseEventDataItems,
          },
        });
        Utils.sendCustomDataLayerEvent({
          event: !isRenewal
            ? CustomDataEvents[CustomDataEvents.quote_bought_buy]
            : CustomDataEvents[CustomDataEvents.quote_bought_renewal],
        });
        // prevent resending GA events on React re-render of App on same page
        window.kbSchema.has_finalised_purchase = true;
      } else {
        const optionalDataLayerProps: { [key: string]: string | number } = Utils
          .getOptionalDataLayerProps(quoteData);
        Utils.sendDataLayerEvent({
          ...optionalDataLayerProps,
          event:
            DataEvents[DataEvents.analyticsEvent],
          eventCategory:
            DataCategories[DataCategories['Quote Journey']],
          eventAction:
            DataActions[DataActions.Progression],
          stepNumber: outerStep + 1,
          stepName: quoteData.steps[(outerStep)].steps[innerStep].title,
          substage: innerStep + 1,
        });
        // new GA4 custom events
        if (outerStep === 0 && innerStep === 0) {
          Utils.sendCustomDataLayerEvent({
            ...optionalDataLayerProps,
            event: !isRenewal
              ? CustomDataEvents[CustomDataEvents.quote_started_buy]
              : CustomDataEvents[CustomDataEvents.quote_started_renewal],
          });
        }
        if (outerStep === 1 && innerStep === 0) {
          Utils.sendCustomDataLayerEvent({
            ...optionalDataLayerProps,
            event: !isRenewal
              ? CustomDataEvents[CustomDataEvents.quote_seen_buy]
              : CustomDataEvents[CustomDataEvents.quote_seen_renewal],
          });
        }
        if (outerStep === 3 && innerStep === 0) {
          Utils.sendCustomDataLayerEvent({
            ...optionalDataLayerProps,
            event: !isRenewal
              ? CustomDataEvents[CustomDataEvents.quote_details_collected_buy]
              : CustomDataEvents[CustomDataEvents.quote_details_collected_renewal],
          });
        }
      }
    },
    [innerStep, outerStep],
  );

  function renderStep(step: Step, index: number) {
    const colClass = 'col-3 col-sm-2';
    const activeClass = stepObject.step === index ? 'active col-sm-6' : '';
    const completeClass = stepObject.step > index ? 'complete' : '';
    let innerProgress = (stepObject.innerStep + 1) / steps[stepObject.step].steps.length;
    innerProgress = (stepObject.step > index) ? 1 : innerProgress;
    return (
      <div key={index} className={`step ${colClass} ${activeClass} ${completeClass}`}>
        <div className="d-flex flex-column">
          <div className="p-1 step__title hide-on-mobile">{step.title}</div>
          <div className="p-1">
            <div className="progress">
              <div
                className="progress-bar"
                aria-valuenow={innerProgress * 100}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{ width: `${innerProgress * 100}%` }}
              />
            </div>
          </div>
          {stepObject.step === index
            ? (
              <div className="p-1">
                {step.steps[stepObject.innerStep].title}
              </div>
            )
            : (
              <div className="p-1">
                {step.steps[0].title}
              </div>
            )}

        </div>
      </div>
    );
  }

  function renderMobileWideStep(step: Step) {
    const colClass = 'active col-12 mobile-only';
    const innerProgress = (stepObject.innerStep + 1) / steps[stepObject.step].steps.length;
    return (
      <div className={`step ${colClass}`}>
        <div className="d-flex flex-column">
          <div className="p-1 step__title">{step.title}</div>
          <div className="p-1">
            <div className="progress">
              <div
                className="progress-bar"
                aria-valuenow={innerProgress * 100}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{ width: `${innerProgress * 100}%` }}
              />
            </div>
          </div>
          <div className="p-1">
            {step.steps[stepObject.innerStep].title}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-progress p-4">
      <div className="container">
        <div className="row wizard-progress__row">
          {renderMobileWideStep(steps[stepObject.step])}
          {steps.map(renderStep)}
        </div>
      </div>
    </div>
  );
}
Progress.defaultProps = {
  step: 0,
};

export default Progress;
